import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Check, CalendarIcon, ArrowRight, ArrowLeft, Store, Dog, PawPrint, Leaf, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { PHONE_1 } from "@/i18n/translations";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { isDateDisabled, loadClosures } from "@/lib/hours";
import { normalizePhone, isValidGrPhone, isValidEmail } from "@/lib/phone";

export const SelfWashBookingDialog = ({ open, onOpenChange, plan }) => {
  const { t } = useLang();
  const b = t.selfwash.booking;
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ customer_name: "", surname: "", phone: "", email: "", notes: "" });
  const [nagayu, setNagayu] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState({ status: "idle", label: null, reason: null, kind: null, amount: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [, setClosuresTick] = useState(0);

  const baseTotal = plan ? plan.price + (nagayu ? 10 : 0) : 0;
  const finalTotal = couponState.status === "valid"
    ? couponState.kind === "percent"
      ? Math.max(0, Math.round(baseTotal * (1 - couponState.amount / 100)))
      : Math.max(0, baseTotal - Math.floor(couponState.amount))
    : baseTotal;

  const checkCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!form.phone) { toast.error(b.coupon_fill_phone); return; }
    setCouponState((s) => ({ ...s, status: "checking" }));
    try {
      const res = await api.get(`/coupons/validate?code=${encodeURIComponent(couponCode.trim())}&phone=${encodeURIComponent(form.phone)}&service=selfwash`);
      if (res.data.valid) setCouponState({ status: "valid", label: res.data.label, reason: null, kind: res.data.kind, amount: res.data.amount });
      else setCouponState({ status: "invalid", label: null, reason: res.data.reason, kind: null, amount: 0 });
    } catch {
      setCouponState({ status: "invalid", label: null, reason: "Error", kind: null, amount: 0 });
    }
  };

  useEffect(() => {
    if (open) loadClosures().then(() => setClosuresTick((v) => v + 1));
  }, [open]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setDate(null);
      setTime("");
      setSlots(null);
      setForm({ customer_name: "", surname: "", phone: "", email: "", notes: "" });
      setNagayu(false);
      setCouponCode("");
      setCouponState({ status: "idle", label: null, reason: null, kind: null, amount: 0 });
      if (plan) track("selfwash_start", { form: "selfwash", plan: plan.key });
    }
  }, [open, plan]);

  const loadSlots = useCallback(async (d) => {
    setLoadingSlots(true);
    setTime("");
    try {
      const res = await api.get(`/selfwash/availability?date=${format(d, "yyyy-MM-dd")}`);
      setSlots(res.data);
    } catch {
      setSlots({ available: [], all_slots: [], taken: [] });
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  const onPickDate = (d) => {
    setDate(d);
    if (d) loadSlots(d);
  };

  const submit = async () => {
    if (!form.customer_name || !form.phone) {
      toast.error(b.required);
      return;
    }
    if (!isValidGrPhone(form.phone)) {
      toast.error(b.phone_error);
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error(b.email_error);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/selfwash-bookings", {
        customer_name: form.surname.trim() ? `${form.customer_name.trim()} ${form.surname.trim()}` : form.customer_name.trim(),
        first_name: form.customer_name.trim(),
        phone: form.phone,
        email: form.email || null,
        plan: plan.key,
        plan_price: plan.price,
        plan_duration: plan.min,
        nagayu,
        date: format(date, "yyyy-MM-dd"),
        time,
        notes: form.notes || null,
        coupon_code: couponState.status === "valid" ? couponCode.trim().toUpperCase() : null,
      });
      toast.success(b.success);
      track("selfwash_booking", { fb_event: "Schedule", value: plan.price + (nagayu ? 10 : 0), currency: "EUR", nagayu });
      onOpenChange(false);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(b.slot_taken);
        loadSlots(date);
        setStep(2);
      } else {
        toast.error(b.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;
  const field = "rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11";
  const isToday = date && format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  const steps = [b.step_plan, b.step_datetime, b.step_details];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-lg rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="selfwash-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <Dog className="h-6 w-6" /> {b.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">{plan.name} · €{plan.price}</DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex items-center gap-2">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 text-xs font-bold ${step >= i + 1 ? "opacity-100" : "opacity-50"}`}>
                  <span className={`grid place-items-center h-6 w-6 rounded-full text-[11px] ${step >= i + 1 ? "bg-brand-cream text-brand" : "bg-white/20"}`}>
                    {step > i + 1 ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < 2 && <div className="flex-1 h-px bg-brand-cream/25" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <div className="rounded-2xl bg-white border border-brand/15 p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-brand/60">{b.selected_plan}</p>
                    <h3 className="font-heading text-2xl font-bold text-brand-ink">{plan.name}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-brand-ink/60"><Clock className="h-4 w-4" /> {plan.min} {t.selfwash.minutes}</p>
                  </div>
                  <span className="font-heading text-4xl font-bold text-brand">€{plan.price}</span>
                </div>
                <p className="mt-4 text-sm text-brand-ink/70 flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-brand shrink-0" /> {t.selfwash.includes}</p>
                <Button data-testid="sw-step1-next" onClick={() => setStep(2)} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 group">
                  {b.next} <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <Label className="text-brand-ink font-semibold">{b.pick_date}</Label>
                <div className="mt-2 rounded-2xl border border-brand/15 bg-white flex justify-center p-1">
                  <Calendar mode="single" selected={date} onSelect={onPickDate} disabled={isDateDisabled} data-testid="sw-calendar" />
                </div>

                {date && (
                  <div className="mt-5">
                    <Label className="text-brand-ink font-semibold">{b.pick_time}</Label>
                    {loadingSlots ? (
                      <p className="mt-2 text-sm text-brand-ink/50">{b.loading}</p>
                    ) : slots && slots.available.length === 0 ? (
                      <p className="mt-2 text-sm text-rose-600 font-semibold">{b.no_slots}</p>
                    ) : (
                      <div className="mt-2 grid grid-cols-4 gap-2" data-testid="sw-slots">
                        {slots?.all_slots.map((s) => {
                          const booked = slots.taken.includes(s);
                          const unavail = !slots.available.includes(s); // booked, ±2h window or <2h from now
                          return (
                            <button
                              key={s}
                              data-testid={`sw-slot-${s}`}
                              disabled={unavail}
                              onClick={() => setTime(s)}
                              className={`py-2 rounded-xl text-sm font-bold border transition-colors duration-200 ${
                                booked
                                  ? "bg-brand-soft/30 text-brand-ink/30 border-transparent line-through cursor-not-allowed"
                                  : unavail
                                  ? "bg-brand-soft/20 text-brand-ink/30 border-transparent cursor-not-allowed"
                                  : time === s
                                  ? "bg-brand text-brand-cream border-brand"
                                  : "bg-white text-brand-ink border-brand/20 hover:border-brand"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {isToday && (
                      <p className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 font-semibold" data-testid="sw-call-soon">
                        {b.call_soon}{" "}
                        <a href={`tel:${PHONE_1}`} className="underline inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {PHONE_1}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <Button data-testid="sw-step2-back" onClick={() => setStep(1)} variant="outline" className="rounded-full border-brand/30 text-brand font-bold h-12 px-6">
                    <ArrowLeft className="h-4 w-4 mr-2" /> {b.back}
                  </Button>
                  <Button data-testid="sw-step2-next" onClick={() => setStep(3)} disabled={!date || !time} className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 group">
                    {b.next} <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                {/* Nagayu add-on */}
                <div
                  role="button"
                  tabIndex={0}
                  data-testid="sw-nagayu-toggle"
                  onClick={() => setNagayu((v) => !v)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setNagayu((v) => !v); } }}
                  className={`w-full text-left rounded-2xl border p-4 mb-4 flex items-center gap-3 transition-colors duration-200 cursor-pointer ${
                    nagayu ? "border-bamboo bg-bamboo-soft" : "border-brand/15 bg-white hover:border-bamboo/50"
                  }`}
                >
                  <span className={`grid place-items-center h-10 w-10 rounded-full shrink-0 ${nagayu ? "bg-bamboo text-brand-cream" : "bg-bamboo-soft text-bamboo-deep"}`}>
                    <Leaf className="h-5 w-5" />
                  </span>
                  <span className="flex-1">
                    <span className="flex items-center gap-2 font-bold text-brand-ink text-sm">{b.addon_title} <span className="text-bamboo-deep font-extrabold">{b.addon_price}</span></span>
                    <span className="block text-xs text-brand-ink/60 mt-0.5">{b.addon_desc}</span>
                  </span>
                  <Switch checked={nagayu} onCheckedChange={setNagayu} onClick={(e) => e.stopPropagation()} data-testid="sw-nagayu-switch" />
                </div>

                <div className="rounded-2xl bg-white border border-brand/15 p-4 mb-5 text-sm">
                  <p className="font-bold text-brand-ink mb-1">{b.summary}</p>
                  <p className="text-brand-ink/70 flex items-center gap-1.5"><PawPrint className="h-4 w-4 text-brand" /> {plan.name} · €{plan.price} · {plan.min} {t.selfwash.minutes}</p>
                  {nagayu && <p className="text-bamboo-deep flex items-center gap-1.5 mt-1 font-semibold"><Leaf className="h-4 w-4" /> Nagayu CO₂ Spa · +€10</p>}
                  <p className="text-brand-ink/70 flex items-center gap-1.5 mt-1"><CalendarIcon className="h-4 w-4 text-brand" /> {date && format(date, "dd/MM/yyyy")} · {time}</p>
                  {couponState.status === "valid" && (
                    <p className="text-emerald-700 flex items-center gap-1.5 mt-1 font-bold" data-testid="sw-coupon-line">
                      <Check className="h-4 w-4" /> {b.coupon_applied.replace("{label}", couponState.label)} · {couponCode.toUpperCase()}
                    </p>
                  )}
                  <p className="mt-2 pt-2 border-t border-brand/10 flex items-center justify-between font-bold text-brand-ink">
                    <span>{b.total}</span>
                    <span data-testid="sw-total" className="font-heading text-2xl text-brand">
                      {couponState.status === "valid" && <span className="text-sm text-brand-ink/40 line-through mr-2">€{baseTotal}</span>}
                      €{finalTotal}
                    </span>
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-brand-ink font-semibold">{b.name} *</Label>
                      <Input data-testid="sw-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={field} />
                    </div>
                    <div>
                      <Label className="text-brand-ink font-semibold">{b.surname}</Label>
                      <Input data-testid="sw-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className={field} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{b.phone} *</Label>
                    <Input
                      data-testid="sw-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: normalizePhone(e.target.value).slice(0, 10) })}
                      inputMode="tel"
                      maxLength={10}
                      placeholder="69XXXXXXXX"
                      className={`${field} ${form.phone.length > 0 && !isValidGrPhone(form.phone) ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                    />
                    <p className={`mt-1 text-xs ${form.phone.length > 0 && !isValidGrPhone(form.phone) ? "text-rose-600 font-semibold" : "text-brand-ink/50"}`} data-testid="sw-phone-hint">
                      {form.phone.length > 0 && !isValidGrPhone(form.phone) ? b.phone_error : b.phone_hint}
                    </p>
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{b.email} *</Label>
                    <Input data-testid="sw-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{b.coupon_label}</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="sw-coupon"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState({ status: "idle", label: null, reason: null, kind: null, amount: 0 }); }}
                        placeholder="CFM-XXXX"
                        className={`${field} font-mono`}
                      />
                      <Button type="button" onClick={checkCoupon} disabled={!couponCode.trim() || couponState.status === "checking"} variant="outline" className="rounded-xl border-brand/25 text-brand font-bold h-11 shrink-0" data-testid="sw-coupon-check">
                        {b.coupon_check}
                      </Button>
                    </div>
                    {couponState.status === "invalid" && (
                      <p className="mt-1.5 text-xs font-semibold text-rose-600" data-testid="sw-coupon-error">{couponState.reason}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{b.notes}</Label>
                    <Textarea data-testid="sw-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand min-h-[70px]" />
                  </div>
                </div>
                <div className="mt-4 rounded-xl bg-brand-soft/50 border border-brand/10 p-3 flex items-center gap-2 text-sm font-semibold text-brand">
                  <Store className="h-4 w-4" /> {b.payment_note}
                </div>
                <div className="mt-5 flex gap-3">
                  <Button data-testid="sw-step3-back" onClick={() => setStep(2)} variant="outline" className="rounded-full border-brand/30 text-brand font-bold h-12 px-6">
                    <ArrowLeft className="h-4 w-4 mr-2" /> {b.back}
                  </Button>
                  <Button data-testid="sw-confirm-btn" onClick={submit} disabled={submitting} className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12">
                    <Check className="h-4 w-4 mr-2" /> {submitting ? b.confirming : b.confirm}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
